import { useEffect, useState } from 'react';
import { RudderAnalytics } from '@rudderstack/analytics-js';
import { env, isDev, isTest } from '~/env';
import { trackRumEvent } from '~/utilities/configureDatadogRum';

export const PAGE_CATEGORY = 'Analytics';
let analytics: RudderAnalytics | null = null;

const devAnalytics = new Proxy(
  {},
  {
    get: function (_target, prop) {
      return (...args: unknown[]) => {
        if (prop !== 'toJSON' && !isTest()) {
          // eslint-disable-next-line no-console
          console.debug('[analytics]', prop, args);
        }
        if (prop.toString().startsWith('get')) {
          return `dev: ${prop.toString()}`;
        }
        return undefined;
      };
    },
  }
) as RudderAnalytics;

export const trackEvent = (
  eventName: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  properties: Record<string, any> = {}
) => {
  if (!analytics) {
    // eslint-disable-next-line no-console
    console.warn(
      '❗️trackEvent called before analytics initialized:',
      eventName
    );
    return;
  }
  analytics.track(eventName, properties);
  // also track with DataDog RUM
  trackRumEvent(eventName, properties);
};

export const trackPageView = (
  name: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  properties: Record<string, any> = {}
) => {
  if (!analytics) {
    // eslint-disable-next-line no-console
    console.warn('❗️trackPageView called before analytics initialized:', name);
    return;
  }
  analytics.page(PAGE_CATEGORY, name, properties);
};

export const useAnalytics = () => {
  const [analyticsInitialized, setAnalyticsInitialized] =
    useState<boolean>(false);

  useEffect(() => {
    const initAnalyticsAndTrack = async () => {
      if (isDev()) {
        analytics = devAnalytics;
        setAnalyticsInitialized(true);
        return;
      }

      if (analytics) return;

      analytics = new RudderAnalytics();
      analytics.load(
        env.VITE_RUDDERSTACK_WRITE_KEY,
        env.VITE_RUDDERSTACK_DATA_PLANE_URL,
        {
          configUrl: env.VITE_RUDDERSTACK_CONTROL_PLANE_URL,
        }
      );

      await new Promise<void>((resolve) => {
        analytics?.ready(() => {
          // eslint-disable-next-line no-console
          console.log('✅ RudderStack is ready');
          setAnalyticsInitialized(true);
          setupReturningUserTracking();
          resolve();
        });
      });
    };

    initAnalyticsAndTrack();
  }, []);

  return analyticsInitialized;
};

const setupReturningUserTracking = () => {
  const anonId = analytics?.getAnonymousId();
  const prevAnonId = localStorage.getItem('rudder_anonymous_id');

  const isReturning = prevAnonId === anonId;
  if (isReturning) {
    trackEvent('mpl_user_returned', { returning_anonymous_id: anonId });
    analytics?.setAnonymousId(anonId);
  } else if (anonId) {
    localStorage.setItem('rudder_anonymous_id', anonId);
  }
};

export { analytics };
